/**
 * File types we can open in the in-browser template flow (extract text → fill → export in original format when possible).
 * PDF, spreadsheets, and images are excluded.
 */
const FILL_ONLINE_EXTENSIONS = new Set([
  'docx',
  'doc',
  'rtf',
  'txt',
  'md',
  'html',
  'htm',
  'json',
]);

export function getTemplateFillExtension(fileName) {
  if (!fileName || typeof fileName !== 'string') return '';
  const parts = fileName.split('.');
  if (parts.length < 2) return '';
  return parts.pop().toLowerCase();
}

export function isTemplateFillOnlineSupported(fileName) {
  const ext = getTemplateFillExtension(fileName);
  return Boolean(ext && FILL_ONLINE_EXTENSIONS.has(ext));
}

function escapeRegExp(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Replace common placeholder shapes in plain text (preview / RTF-derived text / mammoth extract).
 */
export function replacePlaceholdersInPlainText(content, pairs) {
  let out = String(content ?? '');
  const list = (pairs || []).filter((p) => p.key?.trim());
  for (const { key, value } of list) {
    const v = value == null ? '' : String(value);
    if (!v.trim()) continue; // optional: leave template tokens unchanged when empty
    const k = key.trim();
    const forms = [`<<${k}>>`, `«${k}»`, `{{${k}}}`, `[${k}]`, `{${k}}`];
    for (const token of forms) {
      const rx = new RegExp(escapeRegExp(token), 'gi');
      out = out.replace(rx, v);
    }
  }
  return out;
}

/**
 * Escape user-supplied text for insertion into an RTF stream (control symbols + Unicode).
 */
export function escapeRtfReplacementText(value) {
  const toSignedU16 = (code) => (code > 32767 ? code - 65536 : code);
  let out = '';
  for (const ch of String(value ?? '')) {
    const c = ch.codePointAt(0);
    if (c === 0x5c) out += '\\\\';
    else if (c === 0x7b) out += '\\{';
    else if (c === 0x7d) out += '\\}';
    else if (c === 0x0a) out += '\\par ';
    else if (c === 0x0d) {
      /* skip CR; LF handled above */
    } else if (c <= 0x7f) out += ch;
    else if (c <= 0xffff) out += `\\u${toSignedU16(c)}?`;
    else {
      const hi = Math.floor((c - 0x10000) / 0x400) + 0xd800;
      const lo = ((c - 0x10000) % 0x400) + 0xdc00;
      out += `\\u${toSignedU16(hi)}?\\u${toSignedU16(lo)}?`;
    }
  }
  return out;
}

/**
 * Replace placeholders in the **raw RTF** string so layout/pictures/tables from the source file stay intact.
 * Replacement values are RTF-escaped. Skips empty values like {@link replacePlaceholdersInPlainText}.
 */
export function replacePlaceholdersInRtf(rawRtf, pairs) {
  let out = String(rawRtf ?? '');
  const list = (pairs || []).filter((p) => p.key?.trim());
  for (const { key, value } of list) {
    const v = value == null ? '' : String(value);
    if (!v.trim()) continue;
    const escaped = escapeRtfReplacementText(v);
    const k = key.trim();
    const forms = [`<<${k}>>`, `«${k}»`, `{{${k}}}`, `[${k}]`, `{${k}}`];
    for (const token of forms) {
      const rx = new RegExp(escapeRegExp(token), 'gi');
      out = out.replace(rx, escaped);
    }
  }
  return out;
}

/** MIME type for downloaded filled template (browser hint). */
export function mimeTypeForFilledTemplate(ext) {
  switch (String(ext || '').toLowerCase()) {
    case 'rtf':
      return 'application/rtf';
    case 'html':
    case 'htm':
      return 'text/html;charset=UTF-8';
    case 'json':
      return 'application/json;charset=UTF-8';
    case 'md':
      return 'text/markdown;charset=UTF-8';
    case 'txt':
    default:
      return 'text/plain;charset=UTF-8';
  }
}
